import React, { useState } from "react";
import { axiosPrivate } from "../../../api/axios";

const COMMAND_TYPES = [
  { value: "force_sync", label: "Force Sync", description: "Trigger MySolon sync immediately" },
  { value: "clear_cache", label: "Clear Cache", description: "Clear app cache and old data" },
  { value: "trigger_update", label: "Trigger Update", description: "Check for app updates" },
  { value: "kill_session", label: "Kill Session", description: "Force user logout" },
  { value: "restart_browser", label: "Restart Browser", description: "Restart Puppeteer browser" },
  { value: "reset_queue", label: "Reset Queue", description: "Reset stuck queue items to pending" },
  { value: "force_backup", label: "Force Backup", description: "Trigger database backup" },
  { value: "show_message", label: "Show Message", description: "Display a custom message to user" },
  { value: "restart_app", label: "Restart App", description: "Restart the desktop app" },
];

const DesktopCommands = () => {
  const [type, setType] = useState("force_sync");
  const [targetEmails, setTargetEmails] = useState("");
  const [expiresInMinutes, setExpiresInMinutes] = useState("");
  const [messageTitle, setMessageTitle] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const selectedCommand = COMMAND_TYPES.find((c) => c.value === type);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = { type };

      // Add target emails if specified
      if (targetEmails.trim()) {
        payload.targetEmails = targetEmails
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email);
      }

      // Add expiration if specified
      if (expiresInMinutes) {
        payload.expiresInMinutes = parseInt(expiresInMinutes, 10);
      }

      // Add params for show_message command
      if (type === "show_message") {
        payload.params = {
          title: messageTitle,
          message: messageBody,
        };
      }

      await axiosPrivate.post("/admin/desktop/commands/create", payload);
      setSuccess(`Command "${selectedCommand.label}" sent successfully`);

      // Reset form
      setTargetEmails("");
      setExpiresInMinutes("");
      setMessageTitle("");
      setMessageBody("");

      // Clear success after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to create command");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Send Command Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Send Command to Desktop Apps</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Command Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Command Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {COMMAND_TYPES.map((cmd) => (
                <option key={cmd.value} value={cmd.value}>
                  {cmd.label}
                </option>
              ))}
            </select>
            {selectedCommand && (
              <p className="text-sm text-gray-500 mt-1">{selectedCommand.description}</p>
            )}
          </div>

          {/* Show Message Params */}
          {type === "show_message" && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Message Title
                </label>
                <input
                  type="text"
                  value={messageTitle}
                  onChange={(e) => setMessageTitle(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Message title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Message Body
                </label>
                <textarea
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Message content"
                />
              </div>
            </div>
          )}

          {/* Target Emails */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Target Emails (optional)
            </label>
            <textarea
              value={targetEmails}
              onChange={(e) => setTargetEmails(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="email1@example.com, email2@example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to send to all desktop app users
            </p>
          </div>

          {/* Expires In */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Expires In (minutes, optional)
            </label>
            <input
              type="number"
              value={expiresInMinutes}
              onChange={(e) => setExpiresInMinutes(e.target.value)}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 60 for 1 hour"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty for no expiration
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={creating}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {creating ? "Sending..." : "Send Command"}
          </button>
        </form>
      </div>

      {/* Command Reference */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Command Reference</h3>
        <div className="space-y-2">
          {COMMAND_TYPES.map((cmd) => (
            <div key={cmd.value} className="flex items-start py-2 border-b last:border-b-0">
              <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono mr-3 shrink-0">
                {cmd.value}
              </code>
              <span className="text-sm text-gray-600">{cmd.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-yellow-600 mr-2 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h4 className="font-medium text-yellow-800">Warning</h4>
            <p className="text-sm text-yellow-700 mt-1">
              Commands are executed immediately on connected desktop apps. Use with
              caution, especially "Kill Session" and "Restart App" commands.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopCommands;
